import * as Yup from 'yup';
import _ from 'lodash';

import {
  ALL_QUERY_PARAMS,
  LEGACY_PARAMS_TO_CURRENT_PARAMS,
  SPECIAL_LEGACY_PARAMS_TO_CURRENT_PARAMS,
} from '../server/lib/types/request';
import { ALL_CTA_TYPES } from '../server/lib/types/segment/ctaType';
import { ALL_FINISH_TYPES } from '../server/lib/types/segment/finishType';
import { ALL_MONETIZABLE_TYPES } from '../server/lib/types/segment/monetizableType';

const validators = {
  calling_deployable_name: Yup.string().required('Parameter is required.'),
  calling_deployable_version: Yup.string().required('Parameter is required.'),
  cta_type: Yup.string()
    .required('Parameter is required.')
    .test(
      'case-insensitive-match',
      `This must be one of the following values: ${ALL_CTA_TYPES.join(', ')}.`,
      (value) => {
        if (!value) return false;
        return ALL_CTA_TYPES.some(
          (allowedValue) => allowedValue.toLowerCase() === value.toLowerCase()
        );
      }
    ),
  finish_type: Yup.string()
    .required('Parameter is required.')
    .test(
      'case-insensitive-match',
      `This must be one of the following values: ${ALL_FINISH_TYPES.join(
        ', '
      )}.`,
      (value) => {
        if (!value) return false;
        return ALL_FINISH_TYPES.some(
          (allowedValue) => allowedValue.toLowerCase() === value.toLowerCase()
        );
      }
    ),
  impression_page_url: Yup.string().test(
    'is-valid-url-encoding',
    'This must be a valid url encoding.',
    (value) => {
      if (value === undefined) return true; // not a required param
      if (value === '') return false; // empty string is not valid
      try {
        const decodedValue = decodeURIComponent(value);
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const url = new URL(decodedValue);
        return true;
      } catch {
        return false;
      }
    }
  ),
  impression_pageview_id: Yup.string().required('Parameter is required.'),
  impression_position: Yup.string().required('Parameter is required.'),
  impression_section: Yup.string().required('Parameter is required.'),
  monetizable: Yup.string().test(
    'case-insensitive-match',
    `This must be one of the following values: ${ALL_MONETIZABLE_TYPES.join(
      ', '
    )}.`,
    (value) => {
      if (value === undefined) return true; // not a required param
      if (value === '') return false; // empty string is not valid
      return ALL_MONETIZABLE_TYPES.some(
        (allowedValue) => allowedValue.toLowerCase() === value.toLowerCase()
      );
    }
  ),
  sku_impression_id: Yup.string().required('Parameter is required.'),
};

export enum QueryParamErrorLevel {
  WARN = 'warn',
  ERROR = 'error',
}

export interface QueryParamError {
  message: string;
  level: QueryParamErrorLevel;
}

export function validateQueryParams(queryParams: Record<string, any>): {
  errors: Map<string, QueryParamError>;
} {
  const errors = new Map<string, QueryParamError>();

  // Check for deprecated params
  Object.entries(LEGACY_PARAMS_TO_CURRENT_PARAMS).forEach(
    ([deprecatedParam, currentParams]) => {
      const containsReplacementParam =
        _.intersection(currentParams, Object.keys(queryParams)).length > 0;
      // legacy param is present as well as a replacement param
      if (deprecatedParam in queryParams && containsReplacementParam) {
        errors.set(deprecatedParam, {
          message: `Parameter is deprecated, and has no effect. Please remove it. One or more of the following replacement parameters is already included in the request: ${currentParams.join(
            ', '
          )}.`,
          level: QueryParamErrorLevel.WARN,
        });
        // legacy param is present that requires a replacement
      } else if (deprecatedParam in queryParams && !containsReplacementParam) {
        errors.set(deprecatedParam, {
          message: `Parameter is deprecated. Please remove it, and replace it with one of the following instead: ${currentParams.join(
            ', '
          )}.`,
          level: QueryParamErrorLevel.WARN,
        });
      }
    }
  );

  // Check for special deprecated params
  Object.entries(SPECIAL_LEGACY_PARAMS_TO_CURRENT_PARAMS).forEach(
    ([deprecatedParam, currentParams]) => {
      if (deprecatedParam in queryParams) {
        errors.set(deprecatedParam, {
          message: `Parameter is deprecated. Please consider removing it, and use one of the following instead: ${currentParams.join(
            ', '
          )}.The new recommended parameters are not a drop in replacement, and will need testing to ensure they work as expected.`,
          level: QueryParamErrorLevel.WARN,
        });
      }
    }
  );

  // Check for unknown params
  Object.keys(queryParams).forEach((param) => {
    if (!ALL_QUERY_PARAMS.includes(param)) {
      errors.set(param, {
        message: `Parameter has no effect. Please remove it.`,
        level: QueryParamErrorLevel.WARN,
      });
    }
  });

  // Check for duplicate, required, and invalid params
  Object.keys(validators).forEach((param) => {
    const validator = validators[param as keyof typeof validators];
    const value = queryParams[param];
    try {
      validator.validateSync(value);
    } catch (error) {
      if (error instanceof Yup.ValidationError) {
        errors.set(param, {
          message: _.capitalize(error.message),
          level: QueryParamErrorLevel.ERROR,
        });
      }
    }
  });

  return { errors };
}
